import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import { ArrowLeft } from "lucide-react";

const UnknownSpecialty = () => {
    const { t } = useLang();
    return (
    <div className="pt-40 pb-32 text-center container-site">
        <p className="eyebrow text-moss">{t("specialty.unknown.eyebrow", "Specialty not found")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-5 text-forest">
            {t("specialty.unknown.title", "We couldn't find that specialty page.")}
        </h1>
        <Link
            to="/specialties"
            data-testid="specialty-unknown-back"
            className="btn-primary mt-10 inline-flex"
        >
            {t("specialty.unknown.back", "Explore Specialties")}
        </Link>
    </div>
    );
};

export const SpecialtyDetail = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { SPECIALTY_DETAILS, SPECIALTY_COORDINATION } = siteData;
    const { slug } = useParams();
    const data = SPECIALTY_DETAILS[slug];
    usePageMeta(
        data ? `${data.name} ${t("specialty_detail.metaSuffix", "— Specialty care in India")}` : t("specialty_detail.metaTitle", "Specialty care"),
        t("specialty_detail.metaDesc", "UniversHeal coordinates specialist care in India — connect with experienced specialists, understand your options and plan treatment with a dedicated coordination team.")
    );

    if (!data) return <UnknownSpecialty />;

    return (
        <>
            <PageHero
                eyebrow={`${data.name} ${t("specialty_detail.eyebrowSuffix", "— Specialty care")}`}
                title={
                    <>
                        {data.heroTitle}{" "}
                        <span className="italic text-pine">{t("specialty_detail.inIndia", "in India")}</span>
                    </>
                }
                intro={data.intro}
            >
                <Reveal delay={0.2}>
                    <p
                        className="mt-6 font-serif italic text-2xl sm:text-3xl text-pine"
                        data-testid="specialty-accent"
                    >
                        {data.heroAccent}
                    </p>
                </Reveal>
            </PageHero>

            <section className="pb-8">
                <div className="container-site">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-64 sm:h-[440px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG[data.image]}
                                alt={data.imageAlt}
                                data-testid="specialty-hero-image"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-10">
                <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    <div>
                        <Reveal>
                            <p className="eyebrow text-moss flex items-center gap-3">
                                <span className="font-serif italic text-base tracking-normal text-natural">
                                    01
                                </span>
                                {data.conditionsTitle}
                            </p>
                            <h2 className="font-serif font-medium text-3xl sm:text-4xl text-forest mt-5 max-w-md leading-tight">
                                {tHtml("specialty_detail.conditionsHeading", <>Care that starts with{" "}
                                <span className="italic text-pine">understanding.</span></>)}
                            </h2>
                        </Reveal>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {data.conditions.map((c, i) => (
                                <Reveal key={c} delay={i * 0.04}>
                                    <span
                                        data-testid={`specialty-condition-${i + 1}`}
                                        className="inline-flex items-center gap-2 bg-white border border-pine/15 rounded-xl px-4 py-2.5 text-sm text-charcoal/85"
                                    >
                                        <LeafMark className="w-3.5 h-3.5 text-natural shrink-0" />
                                        {c}
                                    </span>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-xs leading-relaxed text-moss">
                                {t("specialty_detail.indicativeNote", "This list is indicative, not exhaustive. If your concern is not listed, write to us — we will tell you honestly how we can help.")}
                            </p>
                        </Reveal>
                    </div>

                    <div>
                        <Reveal>
                            <p className="eyebrow text-moss flex items-center gap-3">
                                <span className="font-serif italic text-base tracking-normal text-natural">
                                    02
                                </span>
                                {t("specialty_detail.whatWeCoordinate", "What UniversHeal coordinates")}
                            </p>
                            <h2 className="font-serif font-medium text-3xl sm:text-4xl text-forest mt-5 max-w-md leading-tight">
                                {tHtml("specialty_detail.coordHeading", <>One team, <span className="italic text-pine">every step.</span></>)}
                            </h2>
                        </Reveal>
                        <div className="mt-8">
                            {SPECIALTY_COORDINATION.map((item, i) => (
                                <Reveal key={item} delay={i * 0.04}>
                                    <div
                                        data-testid={`specialty-coordinate-${i + 1}`}
                                        className="flex items-center gap-4 py-4 border-t border-pine/10"
                                    >
                                        <span className="font-serif italic text-lg text-natural w-8 shrink-0">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <p className="text-[15px] text-charcoal/85">{item}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-xs leading-relaxed text-moss bg-sage/70 border border-pine/10 rounded-xl px-5 py-4">
                                {t("specialty_detail.independenceNote", "UniversHeal coordinates access to healthcare services. Diagnosis and treatment are provided by independent, licensed specialists and hospitals.")}
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <CtaBand
                headline={t("specialty_detail.ctaHeadline", "Considering this specialty for your care?")}
                text={t("specialty_detail.text", "Start with a medical opinion — share your case and we will coordinate the right specialist for you.")}
            />
        </>
    );
};
